<div class="container-fluid readiness-guide-page">
  <!-- Header -->
  <div class="row align-items-center hero-row">
    <button class="nav-back" (click)="goBack()">
      <fa-icon [icon]="faChevronLeft"></fa-icon>
    </button>
    <h1 class="title">Readiness Form Guide</h1>
  </div>

  <p class="subtitle">
    Click on any highlighted area of the Readiness form to learn what it does and how to use it.
  </p>

  <!-- Search -->
  <div class="row">
    <div class="col-md-6">
      <div class="search-wrapper">
        <input type="text"
               class="form-control form-control-lg"
               [(ngModel)]="searchTerm"
               placeholder="Search features… e.g. 'drag', 'filter', 'ProPak'" />
      </div>
      <div class="search-hint" *ngIf="!searchTerm">
        Tip: try searching for “task”, “filter”, “group”, “ProPak”, or “undo”.
      </div>
    </div>
  </div>

  <!-- Main interactive area -->
  <div class="row main-row">
    <!-- Left: image map -->
    <div class="col-lg-9">
      <div class="map-card">
        <div class="map-title-row">
          <!-- <span class="map-title">
            <span *ngIf="!currentSection">Readiness form overview</span>
            <span *ngIf="currentSection">
              <button class="btn-link" (click)="onImageClick($event, mapImg)" style="padding: 0; font-size: inherit;">
                <fa-icon [icon]="['fas', 'arrow-left']"></fa-icon> Back to overview
              </button>
            </span>
          </span> -->
          <span class="map-subtitle">
            <span *ngIf="!currentSection">Click a section to explore its features in detail.</span>
            <span id="goToOverview" *ngIf="currentSection" (click)="onImageClick($event, mapImg)">Click here or image to return to overview.</span>
          </span>
        </div>

        <div class="map-container">
          <!-- Loading indicator while images preload -->
          <div class="image-loading-overlay" *ngIf="!imagesPreloaded">
            <i class="fa fa-spinner fa-spin"></i>
            <span>Loading guide...</span>
          </div>

          <!-- All images preloaded and stacked - only active one is visible -->
          <img #mapImg
               src="app/assets/readiness-guide/overview-latest.png"
               alt="Readiness form overview"
               class="map-image"
               [ngClass]="{'image-active': !currentSection, 'clickable': false}"
               (click)="onImageClick($event, mapImg)" />
          
          <img *ngFor="let section of sections"
               src="{{section.image}}"
               [alt]="section.title"
               class="map-image section-image"
               [ngClass]="{'image-active': currentSection === section.id, 'clickable': true}"
               (click)="onImageClick($event, mapImg)" />

          <!-- Hotspots (sections in overview, features in drill-down) - only show when image is loaded -->
          <div *ngFor="let item of visibleHotspots; let i = index"
               class="hotspot-wrapper"
               [ngClass]="{'hotspot-visible': imageLoaded}"
               [ngStyle]="{'top.%': item.top, 'left.%': item.left}">
            
            <!-- For Sections (overview mode) -->
            <ng-container *ngIf="isSection(item)">
              <button class="hotspot-button section-hotspot"
                      [style.animation-delay.ms]="i * 150"
                      (click)="openSection(item.id); $event.stopPropagation()"
                      (mouseenter)="hoveredHotspot = item.id"
                      (mouseleave)="hoveredHotspot = null"
                      [attr.aria-label]="'Explore ' + item.title"
                      [title]="item.title">
                <span class="hotspot-label">{{i + 1}}</span>
              </button>
              
              <div class="hotspot-tooltip"
                   [ngClass]="{'visible': hoveredHotspot === item.id}">
                Click to explore: {{item.title}}
              </div>
            </ng-container>
            
            <!-- For Features (drill-down mode) -->
            <ng-container *ngIf="isHotspot(item)">
              <!-- Pulse animation ring for active hotspot -->
              <div class="hotspot-pulse" 
                   *ngIf="item.id === selectedHotspot?.id"></div>
              
              <!-- Outer glow ring -->
              <div class="hotspot-ring" 
                   [ngClass]="{'active': item.id === selectedHotspot?.id}">
              </div>
              
              <!-- Main hotspot button -->
              <button class="hotspot-button"
                      [ngClass]="{
                        'active': item.id === selectedHotspot?.id,
                        'hotspot-animate-in': true
                      }"
                      [style.animation-delay.ms]="i * 150"
                      (click)="selectHotspot(item); $event.stopPropagation()"
                      (mouseenter)="hoveredHotspot = item.id"
                      (mouseleave)="hoveredHotspot = null"
                      [attr.aria-label]="'View ' + item.title"
                      [title]="item.title">
                <span class="hotspot-label">{{item.label}}</span>
              </button>
              
              <!-- Tooltip on hover -->
              <div class="hotspot-tooltip"
                   [ngClass]="{'visible': hoveredHotspot === item.id && item.id !== selectedHotspot?.id}">
                {{item.title}}
              </div>
            </ng-container>
          </div>
        </div>

        <div class="map-legend" *ngIf="!currentSection">
          <span><strong>Click to explore sections:</strong></span>
          <span class="legend-item" *ngFor="let section of visibleHotspots; let i = index"
                role="button" tabindex="0"
                (click)="openSection(section.id); $event.stopPropagation()"
                (keydown.enter)="openSection(section.id); $event.stopPropagation()"
                [attr.aria-label]="'Explore ' + section.title">
            {{i + 1}} - {{section.title}}
          </span>
        </div>
        
        <div class="map-legend" *ngIf="currentSection">
          <span><strong>Features in this section:</strong></span>
          <ng-container *ngFor="let spot of visibleHotspots">
            <span class="legend-item" role="button" tabindex="0"
                  (click)="selectHotspot(spot); $event.stopPropagation()"
                  (keydown.enter)="selectHotspot(spot); $event.stopPropagation()"
                  [ngClass]="{'active': spot.id === selectedHotspot?.id}"
                  [attr.aria-label]="'View ' + spot.title">
              {{spot.label}} - {{spot.title}}
            </span>
          </ng-container>
        </div>
      </div>
    </div>

    <!-- Right: tutorial for selected hotspot -->
    <div class="col-lg-3">
      <div class="detail-card" 
           *ngIf="selectedHotspot"
           [@slideIn]="selectedHotspot.id">
        <div class="detail-header">
          <div class="detail-label-circle pulse-circle">
            {{selectedHotspot.label}}
          </div>
          <div class="detail-title-block">
            <h2 class="detail-title">{{selectedHotspot.title}}</h2>
            <p class="detail-short">{{selectedHotspot.short}}</p>
          </div>
        </div>

        <div class="detail-media" *ngIf="selectedHotspot.detailImage">
          <img [src]="selectedHotspot.detailImage"
               [alt]="selectedHotspot.title"
               class="detail-image" />
        </div>

        <div class="detail-body">
          <h3 class="section-heading">
            <fa-icon [icon]="faBookOpen" class="mr-1"></fa-icon>
            How to use
          </h3>
          <ol class="steps-list">
            <li *ngFor="let step of selectedHotspot.steps; let i = index"
                class="step-item"
                [style.animation-delay.ms]="i * 100">
              {{step}}
            </li>
          </ol>

          <div class="detail-tips" *ngIf="selectedHotspot.tip">
            <fa-icon [icon]="faLightbulb" class="tip-icon"></fa-icon>
            <strong>Tip:</strong> {{selectedHotspot.tip}}
          </div>

          <div class="tag-row" *ngIf="selectedHotspot.tags && selectedHotspot.tags.length">
            <span class="tag-pill" *ngFor="let tag of selectedHotspot.tags">
              {{tag}}
            </span>
          </div>
        </div>
      </div>

      <!-- Feature list / search results -->
      <div class="list-card">
        <div class="list-header">
          <span class="list-title">All features on this form</span>
        </div>
        <div class="feature-list">
          <button *ngFor="let spot of filteredHotspots"
                  class="list-item"
                  [ngClass]="{'active': spot.id === selectedHotspot?.id}"
                  (click)="selectHotspot(spot)">
            <span class="list-label-circle">{{spot.label}}</span>
            <div class="list-text">
              <div class="list-item-title">{{spot.title}}</div>
              <div class="list-item-short">{{spot.short}}</div>
            </div>
          </button>

          <p class="no-results" *ngIf="!filteredHotspots.length">
            No features found for “{{searchTerm}}”.
          </p>
        </div>
      </div>
    </div>
  </div>
</div>
